"use client";

import React, { useState } from "react";
// import Link from "next/link"
import { motion } from "framer-motion";
import {
  AppBar,
  Toolbar,
  Container,
  Typography,
  Button,
  Box,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useMediaQuery,
  Drawer,
  Link,
  CardContent,
  Card,
  CardHeader,
  Paper,
  MenuItem,
  Menu,
  Divider,
  Alert,
} from "@mui/material";
import {
  createTheme,
  styled,
  ThemeProvider,
  useTheme,
} from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import { useNavigate } from "react-router";
import bg1 from "../../assets/bg1.jpg";
import bg2 from "../../assets/bg2.jpg";
import { useUser } from "@/contexts/UserContext";
import HowItWorks from "./components/how-it-work";
import KeyDatesTimeline from "./components/key-date-timeline";
import JudgesSection from "./components/judgesSection";
import RulesAndGuidelines from "./components/rulesGuideLine";
import WhyParticipate from "./components/why-participate";
import FAQComponent from "./components/FAQ";
import { LogOutIcon, UserIcon } from "lucide-react";

import TCC2NDANNOUCEMENT from "../../assets/TCC 2nd annoucement.pdf";
import CampaignIcon from '@mui/icons-material/Campaign';
import TCCDeadlineBanner from "@/components/TCCDeadlineBanner";
import { useSiteAlert } from "@/contexts/SiteAlertContext";

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to right, #0066FF, #5C9DFF)",
  color: "white",
  padding: theme.spacing(10, 0),
  textAlign: "center",
}));

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
}));

const GraySection = styled(Section)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
}));

const LightBlueSection = styled(Section)(({ theme }) => ({
  backgroundColor: "rgba(0, 102, 255, 0.1)",
}));

const Footer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  color: "white",
  padding: theme.spacing(4, 0),
}));

const NavLink = styled(Link)(({ theme }) => ({
  color: "#0066FF",
  textDecoration: "none",
  marginLeft: theme.spacing(3),
  "&:hover": {
    textDecoration: "underline",
  },
  fontFamily: "Roboto",
}));

const theme = createTheme({
  palette: {
    primary: {
      main: "#2952a3", // The blue color from the image
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
  },
  typography: {
    h1: {
      fontWeight: 700,
      color: "#2952a3",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      color: "#2952a3",
      textAlign: "center",
      marginBottom: "2rem",
    },
    h5: {
      fontWeight: 700,
      color: "#2952a3",
    },
    body1: {
      color: "#555555",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: "none",
          padding: "8px 24px",
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          transition: "box-shadow 0.3s ease-in-out",
          height: "100%",
          "&:hover": {
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
          },
        },
      },
    },
  },
});

export default function LandingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const { user } = useUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { message, isActive } = useSiteAlert();
  const isAdminOrOrg = user?.is_admin || user?.is_org;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    document.body.classList.remove("ro-popover-open");
    setAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const navItems = [
    { name: "Overview", href: "#overview" },
    { name: "TCC 2025 Champions", href: "./tcc-winner" },
    { name: "Educatalyst", href: "./educatalyst" },
    { name: "Summit Finalist", href: "./summit-finalist" },
    { name: "TCC Summit 2025", href: "./tcc-summit-2025" },
    { name: "How It Works", href: "#how-it-works" },
    { name: "Timeline", href: "#timeline" },
    { name: "Judges", href: "#judges" },
    { name: "Rules", href: "#rules" },
    { name: "FAQ", href: "#faq" },
  ];

  const mainNavItems = navItems.slice(0, 5);
  const moreNavItems = navItems.slice(5);

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center", p: 2 }}>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.name}
            component={Link}
            href={item.href}
            sx={{ color: "#000", textDecoration: "none" }}
          >
            <ListItemText primary={item.name} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="static"
        color="default"
        elevation={1}
        sx={{ backgroundColor: "white" }}
      >
        <Container>
          <Toolbar disableGutters>
            <Box sx={{ flexGrow: 1 }}>
              <img
                src="https://pythaverse.space/wp-content/uploads/2023/02/18b214a6c0c81d9644d9.png"
                alt="Pythaverse Logo"
                width="200"
                height="50"
                style={{ objectFit: "contain" }}
              />
            </Box>

            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  ModalProps={{
                    keepMounted: true,
                  }}
                >
                  {drawer}
                </Drawer>
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {/* {navItems.map((item) => (
                  <NavLink key={item.name} href={item.href}>
                    {item.name}
                  </NavLink>
                ))} */}
                {mainNavItems.map((item) => (
                  <NavLink key={item.name} href={item.href} sx={{ mx: 1 }}>
                    {item.name}
                  </NavLink>
                ))}

                {/* More button */}
                {moreNavItems.length > 0 && (
                  <>
                    <Button
                      onClick={handleMoreClick}
                      sx={{ textTransform: "none" }}
                    >
                      More
                    </Button>
                    <Menu
                      anchorEl={moreAnchorEl}
                      open={Boolean(moreAnchorEl)}
                      onClose={handleMoreClose}
                    >
                      {moreNavItems.map((item) => (
                        <MenuItem key={item.name} onClick={handleMoreClose}>
                          <NavLink href={item.href}>{item.name}</NavLink>
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                {user?.user_login ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleUserMenuClick}
                    color="primary"
                    title={user?.display_name}
                    sx={{
                      textTransform: "none",
                      borderRadius: "30px",
                      "& .MuiTypography-root": {
                        color: "white",
                      },
                      fontFamily: "Roboto",
                      fontStyle: "normal",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: "19px",
                      textDecoration: "none",
                      color: "#F5F5F5",
                      marginLeft: "50px",
                    }}
                  >
                    <Typography variant="h6" noWrap component="div" ml={1}>
                      {user?.display_name}
                    </Typography>
                  </Button>
                ) : (
                  <NavLink key="btnLogin" href={user?.login_url}>
                    Login
                  </NavLink>
                )}
              </Box>
            )}

            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
            >
              <MenuItem
                onClick={() => (window.location.href = user?.logout_url || "/")}
              >
                <LogOutIcon size={20} />
                <Typography variant="inherit" noWrap component="span" ml={1}>
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </AppBar>
      <TCCDeadlineBanner />
      <ThemeProvider theme={theme}>
        {/* Hero Section */}
        <Box sx={{ bgcolor: "background.default", py: 8 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ maxWidth: 600 }}>
                  <Typography
                    variant="h1"
                    component="h1"
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.5rem", md: "3rem" },
                      color: "#3257b8",
                      fontWeight: 500,
                    }}
                  >
                    Pythaverse Educators
                    <br />
                    ThoughtCoCreate 2025
                  </Typography>
                  <Typography
                    variant="body1"
                    paragraph
                    sx={{ fontSize: "1.1rem", mb: 3 }}
                  >
                    Empowering individual educators to solve AI-era challenges
                    with practical, ethical, and innovative solutions. Join our
                    global community and help shape the future of education.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() =>
                        (window.location.href = `https://${process.env.API_HOST}/tcc-whitepaper-2025`)
                      }
                      sx={{
                        backgroundColor: "primary", // Blue background
                        color: "#FFD700", // Yellow text
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "primary", // Darker blue on hover
                          color: "#FFD700",
                        },
                      }}
                    >
                      TCC White Paper
                    </Button>
                    {user?.user_login == null && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() =>
                          (window.location.href = `https://${process.env.API_HOST}/educator-register`)
                        }
                      >
                        Register Now
                      </Button>
                    )}

                    {user?.role == "educator" && user?.user_login != null ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={() =>
                          (window.location.href = user?.urlWsp || "")
                        }
                      >
                        Workspace
                      </Button>
                    ) : user?.user_login == null ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={() =>
                          (window.location.href = user?.login_url || "")
                        }
                      >
                        Login
                      </Button>
                    ) : (
                      <></>
                    )}

                    {isAdminOrOrg && (
                      <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      onClick={() =>
                        (window.location.href = `https://${process.env.API_HOST}/${process.env.PREFIX_WORKSHOP_ADMIN}`)
                      }
                    >
                      Workshop Management
                    </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      onClick={() =>
                        window.open(process.env.WHATSAPP_SUPPORT || "", "_blank")
                      }
                    >
                      Whatsapp support group
                    </Button>
                    {user?.role_tcc == "judge" && user?.user_login != null && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() =>
                          (window.location.href = `https://${process.env.API_HOST}/tcc-scoring-system`)
                        }
                      >
                        Scoring System
                      </Button>
                    )}
                    {user?.is_org && user?.user_login != null && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() =>
                          (window.location.href = `https://${process.env.API_HOST}/participants`)
                        }
                      >
                        Manage Participants
                      </Button>
                    )}
                  </Box>
                  {/* <a href={TCC2NDANNOUCEMENT} download>
                    <CampaignIcon/> Deadline for the submission of TCC entries is moved to : May 30, 2025
                  </a> */}

                  {isActive && (
                    <Alert
                      icon={<CampaignIcon fontSize="inherit" />}
                      severity="info"
                      sx={{ fontSize: "1rem", alignItems: "center", mt: 1 }}
                    >
                      {/* Deadline for the submission of TCC entries is moved to:{" "}
                      <Link href={TCC2NDANNOUCEMENT} underline="hover" download sx={{ ml: 0.5 }}>
                        May 30, 2025
                      </Link> */}
                      <Box
                        sx={{
                          '& p': { margin: 0 },
                          '& a': { marginLeft: '4px' }, // nếu cần spacing cho link
                        }}
                        dangerouslySetInnerHTML={{
                          __html: `
                            <p>
                              <a href="${TCC2NDANNOUCEMENT}" download style="text-decoration: none;color: #1976d2;">
                                ${message.replace(/\\"/g, '"')}
                              </a>
                            </p>
                          `,
                        }}
                      />
                    </Alert>
                  )}
                  
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Box
                  component="div"
                  sx={{
                    position: "relative",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={bg1}
                    alt="AI-generated image representing Pythaverse Educators ThoughtCoCreate 2025"
                    sx={{
                      width: "100%",
                      objectFit: "cover",
                      borderRadius: 2,
                      boxShadow: 3,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Event Overview Section */}

        <Box
          id="overview"
          sx={{
            position: "relative",
            backgroundImage: `url(${bg2})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            py: 8,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
            <Typography
              variant="h2"
              component="h2"
              align="center"
              gutterBottom
              sx={{
                color: "primary.main",
                fontWeight: 500,
                mb: 6,
              }}
            >
              Event Overview
            </Typography>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      backgroundColor: "#e9ecf2",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      sx={{
                        color: "text.primary",
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      Collaborative Platform
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      A groundbreaking event bringing together passionate
                      educators worldwide to collaborate and innovate.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      backgroundColor: "#e9ecf2",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      sx={{
                        color: "text.primary",
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      Real-World Solutions
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Develop practical solutions to educational problems while
                      leveraging AI and addressing ethical considerations.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      backgroundColor: "#e9ecf2",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      sx={{
                        color: "text.primary",
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      Global Impact
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Shape the future of learning through challenges and
                      collaborative exercises with educators from diverse
                      backgrounds.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </motion.div>
          </Container>
        </Box>

        <HowItWorks />

        <KeyDatesTimeline />

        <JudgesSection />

        <RulesAndGuidelines />

        <WhyParticipate />

        <FAQComponent />
      </ThemeProvider>

      <Footer sx={{backgroundColor: "#3056D3"}}>
        <Container>
          <Grid
            container
            justifyContent="space-between"
            alignItems={{ xs: "center", md: "flex-start" }}
            direction={{ xs: "column", md: "row" }}
          >
            <Grid item xs={12} md={6} sx={{ mb: { xs: 3, md: 0 } }}>
              <Typography variant="h6" gutterBottom>
                Contact & Support
              </Typography>
              <Typography variant="body2">
                Marketing Inquiries: trinh@pythaverse.net
              </Typography>
              <Typography variant="body2">
                Support: linh.dang@pythaverse.net
              </Typography>
            </Grid>

            <Grid item>
              <Box sx={{ display: "flex", gap: 2 }}>
                <IconButton color="inherit" aria-label="LinkedIn">
                  <LinkedInIcon />
                </IconButton>
                <IconButton color="inherit" aria-label="Twitter">
                  <TwitterIcon />
                </IconButton>
                <IconButton color="inherit" aria-label="Facebook">
                  <FacebookIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Footer>
    </Box>
  );
}
